import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { CrepeBuilder } from "@milkdown/crepe/builder"
import { placeholder } from "@milkdown/crepe/feature/placeholder"
import { replaceAll } from "@milkdown/kit/utils"

const DATABASE_NAME = "draftlight-drafts"
const STORE_NAME = "drafts"
const IDLE_SAVE_DELAY = 900
const CONTINUOUS_SAVE_INTERVAL = 10_000
const FEEDBACK_SAVE_TIMEOUT = 10_000

export default class extends Controller {
  static targets = [
    "editor", "source", "status", "recovery", "conflict",
    "feedbackText", "feedbackKind", "feedbackPreview", "feedbackError", "feedbackSubmit", "feedbackSubmitLabel", "feedbackScopeLabel", "feedbackStage",
    "sidebar",
    "feedbackComposer", "feedbackHistory", "historyContextLabel", "historyArchiveToggle"
  ]
  static values = {
    documentId: Number,
    autosaveUrl: String,
    sidebarUrl: String,
    version: Number,
    digest: String,
    reviewedText: String,
    blocks: Array
  }

  async connect() {
    this.serverBody = this.sourceTarget.value
    this.body = this.serverBody
    this.sequence = 0
    this.dirty = false
    this.saving = false
    this.conflicted = false
    this.clientId = this.loadClientId()
    this.database = await this.openDatabase().catch(() => null)

    this.crepe = new CrepeBuilder({
      root: this.editorTarget,
      defaultValue: this.serverBody
    }).addFeature(placeholder, { text: "Begin your draft…", mode: "doc" })

    this.crepe.on((listener) => {
      listener.markdownUpdated((_context, markdown) => this.contentChanged(markdown))
      listener.blur(() => this.flush())
    })

    await this.crepe.create()
    this.blockDecorationObserver = new MutationObserver(() => this.scheduleBlockDecoration())
    this.blockDecorationObserver.observe(this.editorTarget.querySelector(".ProseMirror"), { childList: true, subtree: true })
    this.decorateBlocks()
    this.highlightReviewedText()
    this.showComposer()
    this.showFeedbackFromLocation()
    this.editorPointerHandler = (event) => {
      const block = event.target.closest("p, h1, h2, h3, h4, h5, h6, li, blockquote")
      if (block && this.editorTarget.contains(block)) {
        this.setCurrentBlock(this.addressableElement(block))
      }
    }
    this.editorTarget.addEventListener("pointerdown", this.editorPointerHandler)
    this.editorTarget.addEventListener("click", this.editorPointerHandler)
    this.editorHoverHandler = (event) => this.updateReviewMarker(event)
    this.editorTarget.addEventListener("pointerdown", this.editorHoverHandler)
    this.editorTarget.addEventListener("pointerover", this.editorHoverHandler)
    this.editorTarget.addEventListener("pointermove", this.editorHoverHandler)
    this.editorTarget.addEventListener("pointerout", this.editorHoverHandler)
    await this.checkForLocalRecovery()

    this.heartbeat = window.setInterval(() => this.dirty && this.flush(), CONTINUOUS_SAVE_INTERVAL)
    this.onlineHandler = () => this.flush()
    this.offlineHandler = () => this.setStatus("Offline — saved on this device")
    this.pageHideHandler = () => this.dirty && this.flush({ keepalive: true })
    window.addEventListener("online", this.onlineHandler)
    window.addEventListener("offline", this.offlineHandler)
    window.addEventListener("pagehide", this.pageHideHandler)
  }

  disconnect() {
    window.clearTimeout(this.idleTimer)
    window.clearInterval(this.heartbeat)
    window.removeEventListener("online", this.onlineHandler)
    window.removeEventListener("offline", this.offlineHandler)
    window.removeEventListener("pagehide", this.pageHideHandler)
    this.editorTarget.removeEventListener("pointerdown", this.editorPointerHandler)
    this.editorTarget.removeEventListener("click", this.editorPointerHandler)
    this.editorTarget.removeEventListener("pointerdown", this.editorHoverHandler)
    this.editorTarget.removeEventListener("pointerover", this.editorHoverHandler)
    this.editorTarget.removeEventListener("pointermove", this.editorHoverHandler)
    this.editorTarget.removeEventListener("pointerout", this.editorHoverHandler)
    this.blockDecorationObserver?.disconnect()
    window.clearTimeout(this.blockDecorationTimer)
    window.clearTimeout(this.deferredPointerExitTimer)
    this.crepe?.destroy()
    this.database?.close()
  }

  async submitTitle(event) {
    await this.flush()
    event.target.form.requestSubmit()
  }

  openTutor() {
    this.showComposer()
    this.captureSelection(true)
  }

  captureSelection(includeCurrentParagraph = false) {
    const selectionBlock = this.blockFromSelection()
    const block = this.addressableElement(selectionBlock || (includeCurrentParagraph ? this.currentBlock : null))
    if (!block) return

    const metadata = this.blockElements?.get(block)
    const blockText = metadata?.normalized_text || this.renderedText(block)
    if (!blockText) return

    this.prepareBlockForFeedback(block, { ...metadata, normalized_text: blockText })
  }

  prepareBlockForFeedback(block, metadata) {
    const blockText = metadata?.normalized_text || this.renderedText(block)
    if (!blockText) return

    this.showComposer()
    this.setFeedbackScope("paragraph")
    this.editorTarget.querySelectorAll(".tutor-note-marker.is-visible").forEach((marker) => marker.classList.remove("is-visible"))
    this.currentBlock = block
    if (this.hasFeedbackTextTarget) this.feedbackTextTarget.value = blockText.slice(0, 40_000)
    if (this.hasFeedbackPreviewTarget) {
      this.feedbackPreviewTarget.textContent = blockText
    }
    if (this.hasFeedbackErrorTarget) this.feedbackErrorTarget.classList.add("hidden")
    if (this.hasFeedbackStageTarget) this.feedbackStageTarget.textContent = "Paragraph ready. No request has been sent yet."

    this.editorTarget.querySelectorAll(".tutor-reviewed").forEach((element) => element.classList.remove("tutor-reviewed"))
    block?.classList.add("tutor-reviewed")
  }

  blockFromSelection() {
    const selection = window.getSelection()
    const insideEditor = selection?.rangeCount > 0 &&
      this.editorTarget.contains(selection.anchorNode) &&
      this.editorTarget.contains(selection.focusNode)
    if (!insideEditor) return null

    const element = selection.anchorNode instanceof Element ? selection.anchorNode : selection.anchorNode?.parentElement
    return element?.closest("p, h1, h2, h3, h4, h5, h6, li, blockquote") || null
  }

  setCurrentBlock(block) {
    if (!block) return

    this.currentBlock = block
    this.editorTarget.querySelectorAll(".tutor-review-marker.is-current").forEach((marker) => marker.classList.remove("is-current"))
    const metadata = this.blockElements?.get(block)
    if (!metadata) return

    this.editorTarget.querySelector(`.tutor-review-marker[data-document-block-id="${metadata.id}"]`)?.classList.add("is-current")
  }

  prepareWholeDocument() {
    const article = this.body.trim()
    this.showComposer()
    this.hideHoverMarkers()
    this.currentBlock = null
    this.editorTarget.querySelectorAll(".tutor-reviewed").forEach((element) => element.classList.remove("tutor-reviewed"))
    this.setFeedbackScope("whole_document")
    this.feedbackTextTarget.value = article
    this.feedbackPreviewTarget.textContent = article || "Begin the article before asking for a complete review."
    this.feedbackErrorTarget.classList.add("hidden")
    if (this.hasFeedbackStageTarget) this.feedbackStageTarget.textContent = "Article ready. No request has been sent yet."
  }

  setFeedbackScope(kind) {
    const wholeDocument = kind === "whole_document"
    this.feedbackKindTarget.value = kind
    this.feedbackScopeLabelTarget.textContent = wholeDocument ? "New article review" : "New paragraph review"
    this.feedbackSubmitLabelTarget.textContent = wholeDocument ? "Ask tutor to review article" : "Ask tutor to review"
  }

  highlightReviewedText() {
    if (!this.hasReviewedTextValue || !this.reviewedTextValue.trim()) return

    const reviewed = this.normalizeText(this.reviewedTextValue)
    const block = Array.from(this.blockElements || [])
      .find(([_element, metadata]) => metadata.normalized_text === reviewed)?.[0]
    block?.classList.add("tutor-reviewed")
  }

  decorateBlocks() {
    const serializedBlocks = this.element.dataset.markdownEditorBlocksValue
    const blocks = serializedBlocks ? JSON.parse(serializedBlocks) : this.blocksValue
    if (!blocks?.length) return

    const elements = Array.from(this.editorTarget.querySelectorAll("p, h1, h2, h3, h4, h5, h6, pre"))
      .map((element) => this.addressableElement(element))
      .filter((element, index, all) => element && all.indexOf(element) === index)
    const elementData = elements.map((element, position) => ({
      element,
      position,
      text: this.renderedText(element),
      kind: this.renderedBlockKind(element)
    }))
    const remaining = [...blocks]
    const matches = new Map()

    // Preserve every unchanged identity first, so an inserted or rewritten block
    // cannot shift the identities of unchanged blocks that follow it.
    elementData.forEach((entry) => {
      const matchIndex = remaining.findIndex((block) => block.normalized_text === entry.text)
      if (matchIndex >= 0) matches.set(entry.element, remaining.splice(matchIndex, 1)[0])
    })

    // A rewritten block no longer has an exact digest. Pair it with the closest
    // remaining structural peer; entirely new blocks stay transient until autosave.
    elementData.filter((entry) => !matches.has(entry.element)).forEach((entry) => {
      const candidates = remaining
        .map((block, index) => ({ block, index }))
        .filter(({ block }) => block.kind === entry.kind)
        .sort((left, right) => Math.abs(left.block.position - entry.position) - Math.abs(right.block.position - entry.position))
      const candidate = candidates[0]
      if (candidate) matches.set(entry.element, remaining.splice(candidate.index, 1)[0])
    })

    this.blockElements = new Map()
    this.markerRail?.remove()
    this.markerRail = document.createElement("div")
    this.markerRail.className = "tutor-note-rail"
    this.markerRail.setAttribute("aria-label", "Tutor notes in this draft")
    this.editorTarget.append(this.markerRail)
    const editorRect = this.editorTarget.getBoundingClientRect()

    elementData.forEach(({ element, position, text, kind }) => {
      const persistedBlock = matches.get(element)
      const block = persistedBlock ? { ...persistedBlock, normalized_text: text } : {
        id: `transient-${position}`,
        kind,
        position,
        normalized_text: text,
        feedback_count: 0
      }
      this.blockElements.set(element, block)

      const reviewMarker = document.createElement("button")
      reviewMarker.type = "button"
      reviewMarker.className = "tutor-review-marker"
      reviewMarker.dataset.documentBlockId = block.id
      reviewMarker.textContent = "✦"
      reviewMarker.title = "Choose this paragraph for review"
      reviewMarker.setAttribute("aria-label", `Choose paragraph ${block.position + 1} for review`)
      reviewMarker.style.top = `${element.getBoundingClientRect().top - editorRect.top + 4}px`
      reviewMarker.addEventListener("pointerenter", () => reviewMarker.classList.add("is-visible"))
      reviewMarker.addEventListener("pointerleave", () => reviewMarker.classList.remove("is-visible"))
      reviewMarker.addEventListener("click", (event) => {
        event.preventDefault()
        event.stopPropagation()
        this.prepareBlockForFeedback(element, block)
      })
      this.markerRail.append(reviewMarker)

      if (Number(block.feedback_count) > 0) {
        const marker = document.createElement("button")
        const label = `${block.feedback_count} previous tutor ${Number(block.feedback_count) === 1 ? "note" : "notes"}`
        marker.type = "button"
        marker.className = "tutor-note-marker"
        marker.dataset.documentBlockId = block.id
        marker.textContent = block.feedback_count
        marker.title = `${label} — click to read`
        marker.setAttribute("aria-label", label)
        marker.style.top = `${element.getBoundingClientRect().top - editorRect.top + 4}px`
        marker.addEventListener("click", (event) => {
          event.preventDefault()
          event.stopPropagation()
          this.selectBlockNotes(block.id, marker)
        })
        this.markerRail.append(marker)
      }
    })

    const selectedBlock = this.addressableElement(this.blockFromSelection())
    if (selectedBlock && this.blockElements.has(selectedBlock)) this.setCurrentBlock(selectedBlock)
    this.restoreHoveredMarkers()
  }

  scheduleBlockDecoration() {
    window.clearTimeout(this.blockDecorationTimer)
    this.blockDecorationTimer = window.setTimeout(() => this.decorateBlocks(), 0)
  }

  addressableElement(element) {
    return element?.closest("li, blockquote") || element
  }

  normalizeText(text) {
    return text?.normalize("NFKC").replace(/\s+/g, " ").trim() || ""
  }

  renderedText(element) {
    const copy = element.cloneNode(true)
    copy.querySelectorAll("br").forEach((breakElement) => breakElement.replaceWith(" "))
    return this.normalizeText(copy.textContent)
  }

  renderedBlockKind(element) {
    if (element.matches("li")) return "list_item"
    if (element.matches("blockquote")) return "quote"
    if (element.matches("pre")) return "code_block"
    if (element.matches("h1, h2, h3, h4, h5, h6")) return "heading"

    return "paragraph"
  }

  updateReviewMarker(event) {
    if (event.type !== "pointerout") {
      this.lastPointerX = event.clientX
      this.lastPointerY = event.clientY
    } else if (!this.editorTarget.contains(event.relatedTarget)) {
      this.deferPointerExitCheck()
      return
    }

    const candidate = event.target.closest?.("p, h1, h2, h3, h4, h5, h6, li, blockquote")
    const block = this.addressableElement(candidate) || this.blockAtVerticalPosition(event.clientY)
    const metadata = this.blockElements?.get(block)
    if (!metadata) return

    const reviewMarker = this.editorTarget.querySelector(`.tutor-review-marker[data-document-block-id="${metadata.id}"]`)
    const noteMarker = this.editorTarget.querySelector(`.tutor-note-marker[data-document-block-id="${metadata.id}"]`)
    if (!reviewMarker) return

    if (event.type !== "pointerout") {
      this.showHoverMarkers(reviewMarker, noteMarker)
    }
  }

  deferPointerExitCheck() {
    window.clearTimeout(this.deferredPointerExitTimer)
    this.deferredPointerExitTimer = window.setTimeout(() => {
      const pointerTarget = Number.isFinite(this.lastPointerX) && Number.isFinite(this.lastPointerY) ?
        document.elementFromPoint(this.lastPointerX, this.lastPointerY) : null

      if (pointerTarget && this.editorTarget.contains(pointerTarget)) {
        this.restoreHoveredMarkers()
      } else {
        this.lastPointerX = null
        this.lastPointerY = null
        this.hideHoverMarkers()
      }
    }, 0)
  }

  restoreHoveredMarkers() {
    if (!Number.isFinite(this.lastPointerY)) return

    const block = this.blockAtVerticalPosition(this.lastPointerY)
    const metadata = this.blockElements?.get(block)
    if (!metadata) return

    const reviewMarker = this.editorTarget.querySelector(`.tutor-review-marker[data-document-block-id="${metadata.id}"]`)
    const noteMarker = this.editorTarget.querySelector(`.tutor-note-marker[data-document-block-id="${metadata.id}"]`)
    if (reviewMarker) this.showHoverMarkers(reviewMarker, noteMarker)
  }

  showHoverMarkers(reviewMarker, noteMarker) {
    this.hideHoverMarkers()
    reviewMarker.classList.add("is-visible")
    noteMarker?.classList.add("is-visible")
  }

  blockAtVerticalPosition(clientY) {
    const entries = Array.from(this.blockElements || [])
    return entries.find(([element], index) => {
      const rect = element.getBoundingClientRect()
      const previousRect = entries[index - 1]?.[0].getBoundingClientRect()
      const nextRect = entries[index + 1]?.[0].getBoundingClientRect()
      const top = previousRect ? (previousRect.bottom + rect.top) / 2 : rect.top - 24
      const bottom = nextRect ? (rect.bottom + nextRect.top) / 2 : rect.bottom + 24
      return clientY >= top && clientY < bottom
    })?.[0]
  }

  hideHoverMarkers() {
    this.editorTarget.querySelectorAll(".tutor-review-marker.is-visible, .tutor-note-marker.is-visible")
      .forEach((marker) => marker.classList.remove("is-visible"))
  }

  selectBlockNotes(blockId, activeMarker = null) {
    if (!blockId) return
    blockId = String(blockId)

    this.editorTarget.querySelectorAll(".tutor-note-marker.notes-active").forEach((marker) => marker.classList.remove("notes-active"))
    const marker = activeMarker || this.editorTarget.querySelector(`.tutor-note-marker[data-document-block-id="${blockId}"]`)
    marker?.classList.add("notes-active")

    const notes = Array.from(this.element.querySelectorAll(".feedback-note"))
    const matchingNotes = notes.filter((note) => note.dataset.feedbackBlockIds?.split(" ").includes(blockId))
    this.openHistory(matchingNotes, "paragraph")
  }

  selectArticleNotes() {
    this.editorTarget.querySelectorAll(".tutor-note-marker.notes-active").forEach((marker) => marker.classList.remove("notes-active"))

    const notes = Array.from(this.element.querySelectorAll('.feedback-note[data-feedback-target-kind="whole_document"]'))
    this.openHistory(notes, "whole article")
  }

  openHistory(matchingNotes, subject) {
    this.feedbackComposerTarget.classList.add("hidden")
    this.feedbackHistoryTarget.classList.remove("hidden")
    this.configureHistory(matchingNotes, subject)
    this.setSidebarOpen(true)
  }

  showComposer() {
    this.feedbackComposerTarget.classList.remove("hidden")
    this.feedbackHistoryTarget.classList.add("hidden")
    this.editorTarget.querySelectorAll(".tutor-note-marker.notes-active").forEach((marker) => marker.classList.remove("notes-active"))
  }

  showFeedbackFromLocation() {
    const requestId = window.location.hash.slice(1)
    if (!requestId.startsWith("feedback_request_")) return

    const request = document.getElementById(requestId)
    if (request?.dataset.feedbackTargetKind === "whole_document") {
      this.selectArticleNotes()
      return
    }
    const blockId = request?.dataset.feedbackBlockIds?.split(" ").find(Boolean)
    if (blockId) this.selectBlockNotes(blockId)
  }

  configureHistory(matchingNotes, subject) {
    const notes = Array.from(this.element.querySelectorAll(".feedback-note"))
    notes.forEach((note) => {
      const index = matchingNotes.indexOf(note)
      note.dataset.historyTier = index === 0 ? "latest" : index > 0 ? "archived" : "unrelated"
      note.classList.toggle("hidden", index !== 0)
    })

    this.archiveExpanded = false
    this.archivedNoteCount = Math.max(matchingNotes.length - 1, 0)
    this.historyContextLabelTarget.textContent =
      matchingNotes.length === 0 ? `Waiting for the tutor's note on this ${subject}` :
      matchingNotes.length === 1 ? `Latest note for this ${subject}` :
      `Latest of ${matchingNotes.length} notes for this ${subject}`
    this.updateArchiveToggle()
  }

  setSidebarOpen(open) {
    if (!this.hasSidebarTarget || this.sidebarTarget.checked === open) return

    this.sidebarTarget.checked = open
    this.persistSidebarState()
  }

  async persistSidebarState() {
    if (!this.hasSidebarUrlValue || !this.hasSidebarTarget) return

    await fetch(this.sidebarUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
        "Accept": "application/json"
      },
      body: JSON.stringify({ sidebar_state: { open: this.sidebarTarget.checked } }),
      credentials: "same-origin"
    }).catch(() => undefined)
  }

  toggleArchivedNotes() {
    this.archiveExpanded = !this.archiveExpanded
    this.feedbackHistoryTarget.querySelectorAll('[data-history-tier="archived"]').forEach((note) => {
      note.classList.toggle("hidden", !this.archiveExpanded)
    })
    this.updateArchiveToggle()
  }

  updateArchiveToggle() {
    if (!this.hasHistoryArchiveToggleTarget) return

    this.historyArchiveToggleTarget.classList.toggle("hidden", this.archivedNoteCount === 0)
    if (this.archivedNoteCount > 0) {
      const noun = this.archivedNoteCount === 1 ? "note" : "notes"
      this.historyArchiveToggleTarget.textContent = this.archiveExpanded ? `Hide ${this.archivedNoteCount} earlier ${noun}` : `Show ${this.archivedNoteCount} earlier ${noun}`
    }
  }

  async submitFeedback(event) {
    event.preventDefault()
    if (this.feedbackSubmitting) return

    if (!this.feedbackTextTarget.value.trim()) {
      this.feedbackErrorTarget.textContent = this.feedbackKindTarget.value === "whole_document" ?
        "Write something before asking for a complete review." :
        "Place the cursor in a paragraph before asking for feedback."
      this.feedbackErrorTarget.classList.remove("hidden")
      return
    }

    if (this.feedbackTextTarget.value.length > 40_000) {
      this.feedbackErrorTarget.textContent = "Whole-article reviews currently support drafts up to 40,000 characters."
      this.feedbackErrorTarget.classList.remove("hidden")
      return
    }

    if (this.hasFeedbackSubmitTarget) {
      this.feedbackSubmitTarget.disabled = true
      this.feedbackSubmitTarget.innerHTML = '<span class="loading loading-spinner loading-xs" aria-hidden="true"></span> Saving before sending…'
    }
    if (this.hasFeedbackStageTarget) this.feedbackStageTarget.textContent = "Saving the current draft before contacting the tutor…"

    const saved = await this.withTimeout(this.flush(), FEEDBACK_SAVE_TIMEOUT, false)
    if (!saved) {
      this.resetFeedbackSubmitButton()
      this.feedbackErrorTarget.textContent = this.conflicted ?
        "Resolve the editing conflict before asking for feedback." :
        "Your paragraph could not be saved yet. Check your connection and try again."
      this.feedbackErrorTarget.classList.remove("hidden")
      return
    }

    this.feedbackSubmitting = true
    if (this.hasFeedbackStageTarget) this.feedbackStageTarget.textContent = "Sending to the tutor…"

    const kind = this.feedbackKindTarget.value
    const blockId = this.currentBlock ? this.blockElements?.get(this.currentBlock)?.id : null
    try {
      const response = await fetch(event.target.action, {
        method: "POST",
        headers: { "Accept": "text/vnd.turbo-stream.html" },
        body: new FormData(event.target),
        credentials: "same-origin"
      })
      if ((response.headers.get("Content-Type") || "").includes("vnd.turbo-stream")) {
        Turbo.renderStreamMessage(await response.text())
        await new Promise((resolve) => window.setTimeout(resolve, 0))
      } else if (!response.ok) {
        this.feedbackErrorTarget.textContent = "The tutor could not be reached. Try again."
        this.feedbackErrorTarget.classList.remove("hidden")
      }
      this.finishFeedbackSubmission(response.ok, kind, blockId)
    } catch {
      this.finishFeedbackSubmission(false, kind, blockId)
      this.feedbackErrorTarget.textContent = "The tutor could not be reached. Check your connection and try again."
      this.feedbackErrorTarget.classList.remove("hidden")
    }
  }

  finishFeedbackSubmission(success, kind, blockId) {
    this.feedbackSubmitting = false
    this.resetFeedbackSubmitButton()

    if (success) {
      this.resetComposer()
      if (kind === "whole_document") {
        this.selectArticleNotes()
      } else if (blockId) {
        this.selectBlockNotes(blockId)
      } else {
        this.openHistory([], "paragraph")
      }
    } else if (this.hasFeedbackStageTarget) {
      this.feedbackStageTarget.textContent = kind === "whole_document" ?
        "Article ready. No request has been sent yet." :
        "Paragraph ready. No request has been sent yet."
    }
  }

  resetFeedbackSubmitButton() {
    if (!this.hasFeedbackSubmitTarget) return

    this.feedbackSubmitTarget.disabled = false
    this.feedbackSubmitTarget.innerHTML = `<span aria-hidden="true">✦</span><span data-markdown-editor-target="feedbackSubmitLabel">${this.feedbackKindTarget.value === "whole_document" ? "Ask tutor to review article" : "Ask tutor to review"}</span>`
  }

  resetComposer() {
    this.currentBlock = null
    this.feedbackTextTarget.value = ""
    this.setFeedbackScope("paragraph")
    if (this.hasFeedbackPreviewTarget) this.feedbackPreviewTarget.textContent = "Choose ✦ beside a paragraph to prepare a new review."
    if (this.hasFeedbackStageTarget) this.feedbackStageTarget.textContent = "Choose a paragraph locally, then explicitly ask the tutor to review it."
    if (this.hasFeedbackErrorTarget) this.feedbackErrorTarget.classList.add("hidden")
  }

  async recoverLocal() {
    if (!this.localDraft) return

    this.recoveryTarget.classList.add("hidden")
    this.body = this.localDraft.body
    this.sourceTarget.value = this.body
    this.crepe.editor.action(replaceAll(this.body, true))
    this.dirty = true
    await this.persistLocal()
    this.scheduleSave()
  }

  async discardLocal() {
    this.recoveryTarget.classList.add("hidden")
    this.localDraft = null
    this.body = this.serverBody
    await this.persistLocal(true)
  }

  contentChanged(markdown) {
    this.body = markdown
    this.sourceTarget.value = markdown
    this.dirty = true
    this.setStatus(navigator.onLine ? "Unsaved" : "Offline — saved on this device")
    this.persistLocal()
    this.scheduleBlockDecoration()
    this.scheduleSave()
  }

  scheduleSave() {
    if (this.conflicted) return

    window.clearTimeout(this.idleTimer)
    this.idleTimer = window.setTimeout(() => this.flush(), IDLE_SAVE_DELAY)
  }

  async flush(options = {}) {
    if (this.conflicted) return false
    if (this.saving) {
      const saved = await this.savePromise
      return saved && this.dirty ? this.flush(options) : saved
    }
    if (!this.dirty) return true
    if (!navigator.onLine) {
      this.setStatus("Offline — saved on this device")
      return false
    }

    this.saving = true
    this.sequence += 1
    const body = this.body
    const sequence = this.sequence
    const baseVersion = this.versionValue
    this.setStatus("Saving…")

    this.savePromise = (async () => {
      try {
        const response = await fetch(this.autosaveUrlValue, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
            "Accept": "application/json"
          },
          body: JSON.stringify({
            autosave: {
              client_id: this.clientId,
              sequence,
              base_version: baseVersion,
              body_markdown: body
            }
          }),
          credentials: "same-origin",
          keepalive: options.keepalive === true
        })

        const payload = await response.json()

        if (response.status === 409) {
          this.conflicted = true
          this.conflictTarget.classList.remove("hidden")
          this.setStatus("Conflict")
          this.persistLocal()
          return false
        }

        if (!response.ok) throw new Error(payload.errors?.join(", ") || "Save failed")

        this.versionValue = payload.version
        this.digestValue = payload.digest
        this.dirty = this.body !== body
        this.persistLocal(!this.dirty)
        this.setStatus(this.dirty ? "Unsaved" : "Saved")
        if (this.dirty) this.scheduleSave()
        return true
      } catch (_error) {
        this.dirty = true
        this.setStatus(navigator.onLine ? "Save failed — retrying" : "Offline — saved on this device")
        window.setTimeout(() => this.flush(), 3_000)
        return false
      } finally {
        this.saving = false
      }
    })()

    const saved = await this.savePromise
    this.savePromise = null
    return saved && this.dirty ? this.flush(options) : saved
  }

  withTimeout(promise, milliseconds, fallback) {
    return Promise.race([
      promise,
      new Promise((resolve) => window.setTimeout(() => resolve(fallback), milliseconds))
    ])
  }

  setStatus(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message
  }

  loadClientId() {
    const key = `draftlight-editor-client-${this.documentIdValue}`
    let value = sessionStorage.getItem(key)
    if (!value) {
      value = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`
      sessionStorage.setItem(key, value)
    }
    return value
  }

  async checkForLocalRecovery() {
    const localDraft = await this.readLocal()
    if (!localDraft) return

    this.sequence = Number(localDraft.sequence || 0)
    if (localDraft.body !== this.serverBody) {
      this.localDraft = localDraft
      this.recoveryTarget.classList.remove("hidden")
    }
  }

  openDatabase() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DATABASE_NAME, 1)
      request.onupgradeneeded = () => request.result.createObjectStore(STORE_NAME, { keyPath: "documentId" })
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  readLocal() {
    if (!this.database) return Promise.resolve(null)

    return new Promise((resolve, reject) => {
      const request = this.database.transaction(STORE_NAME).objectStore(STORE_NAME).get(this.documentIdValue)
      request.onsuccess = () => resolve(request.result || null)
      request.onerror = () => reject(request.error)
    }).catch(() => null)
  }

  persistLocal(acknowledged = false) {
    if (!this.database) return Promise.resolve()

    const record = {
      documentId: this.documentIdValue,
      body: this.body,
      sequence: this.sequence,
      version: this.versionValue,
      digest: acknowledged ? this.digestValue : null,
      updatedAt: new Date().toISOString()
    }

    return new Promise((resolve, reject) => {
      const transaction = this.database.transaction(STORE_NAME, "readwrite")
      transaction.objectStore(STORE_NAME).put(record)
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(transaction.error)
    }).catch(() => undefined)
  }
}
